import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Post, PostMedia } from '@/services/posts';
import { Checkbox } from '@/components/ui/Checkbox';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { OCCURRENCE_STATUS_BADGES, getInferredStatus } from '@/utils/calendarStatus';
import { useTimezone } from '@/contexts/TimezoneContext';
import { extractHashtags } from '@/utils/hashtags';
import { ConfirmDialogSimple } from '../ui/ConfirmDialog';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { VariationCreator } from '../VariationCreator';
import { Tooltip } from '@/components/ui/Tooltip';
import { RetryImage } from '@/components/ui';
import { MediaTypeBadge } from '@/components/ui/MediaTypeBadge';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { TextPostTile } from '@/components/ui/TextPostTile';
import { FileThumbnailPlaceholder } from '@/components/ui/FileThumbnailPlaceholder';
import { openReleaseDrawer } from '@/stores/releaseDrawer.store';
import { ROW_ACTION_MENU_DIVIDER, ROW_ACTION_MENU_ITEM, ROW_ACTION_MENU_SURFACE } from '@/components/ui/rowActionMenuStyles';

/**
 * List-view status flags, derived from the SAME canonical status the calendar
 * uses (`getInferredStatus`: displayStatus-first). A post whose canonical
 * status is PUBLISHED must read "Published", never "Mapped": a bare-MAPPED
 * placeholder bucket is suppressed, and the Published badge is forced on even
 * when the `instancesSummary.published` counter is 0 (stale/legacy cache).
 */
const getListStatusFlags = (post: Post): { showMapped: boolean; showPublished: boolean } => {
  const isPublished = getInferredStatus(post) === 'PUBLISHED';
  const summary = post.instancesSummary;
  return {
    showMapped: (summary?.mapped ?? 0) > 0 && !isPublished,
    showPublished: (summary?.published ?? 0) > 0 || isPublished,
  };
};

/**
 * PostTable Component
 *
 * Enhanced table view for posts with columns:
 * - Checkbox for selection
 * - Media thumbnail with type badge and count
 * - Caption with char/word/hashtag counts
 * - Tags (clickable labels)
 * - Versions (dropdown selector + add button)
 * - Workflow Status (conditional, based on approval workflow)
 * - Created At (relative time)
 * - Actions dropdown (with new actions)
 *
 * Library-focused view for content management
 */

interface PostTableProps {
  posts: Post[];
  selectedPosts: number[];
  onSelect: (postId: number) => void;
  onSelectAll: () => void;
  onEdit: (postId: number) => void;
  onDuplicate: (postId: number) => void;
  onDelete: (postId: number) => void;
  onFilterByTag?: (tag: string) => void;
  onSelectVariation?: (postId: number, variationId: string) => void;
  onAddVariation?: (postId: number) => void;
  onScheduleNow?: (postId: number) => void;
  onSendForReview?: (postId: number) => void;
  carteBlanche?: boolean;
  onMarkReady?: (postId: number) => void;
  onMarkDraft?: (postId: number) => void;
  workflowEnabled?: boolean;
  isLoading?: boolean;
  isAgencyTeam?: boolean;
  // Per-post permission guards (backwards-compatible: if not passed, action shows)
  canEditPost?: (post: Post) => boolean;
  canDeletePost?: (post: Post) => boolean;
  canSendForReview?: (post: Post) => boolean;
  canMarkReady?: (post: Post) => boolean;
  canMarkDraft?: (post: Post) => boolean;
  canSchedulePost?: (post: Post) => boolean;
  canDuplicatePost?: (post: Post) => boolean;
}

/** Fallback bottom-nav reserve (nav + air) when --bottom-nav-h can't be read. */
const BOTTOM_NAV_RESERVE_FALLBACK_PX = 96;
/** Air between the bottom nav and the actions menu. */
const BOTTOM_NAV_AIR_PX = 16;

/**
 * Space the fixed bottom nav occupies: --bottom-nav-h (index.css) + safe-area
 * inset + air. The safe-area inset is probed via a throwaway element since
 * env() isn't readable from getComputedStyle directly.
 */
function getBottomNavReservePx(): number {
  const navH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--bottom-nav-h'));
  if (!Number.isFinite(navH)) return BOTTOM_NAV_RESERVE_FALLBACK_PX;
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom)';
  document.body.appendChild(probe);
  const safeArea = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
  probe.remove();
  return navH + safeArea + BOTTOM_NAV_AIR_PX;
}
/** Room the card actions menu wants below its trigger before it flips upward. */
const CARD_MENU_MIN_ROOM_PX = 280;

export const PostTable = ({
  posts,
  selectedPosts,
  onSelect,
  onSelectAll,
  onEdit,
  onDuplicate,
  onDelete,
  onFilterByTag,
  onMarkReady,
  onMarkDraft,
  onScheduleNow,
  onSendForReview,
  workflowEnabled = false,
  isLoading = false,
  canEditPost,
  canDeletePost,
  canSendForReview,
  canMarkReady,
  canMarkDraft,
  canSchedulePost,
  canDuplicatePost,
}: PostTableProps) => {
  const { canCreateVariation } = useReviewerRestrictions();
  const { t } = useTranslation(['posts', 'common']);
  const { formatRelativeDate } = useTimezone();
  const [openMenuPostId, setOpenMenuPostId] = useState<number | null>(null);
  /** Card (mobile) actions menu: open upward when there is no room below the trigger. */
  const [cardMenuUp, setCardMenuUp] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
  /** The actions button that opened the menu: focus returns to it on Escape. */
  const menuTriggerRef = useRef<HTMLElement | null>(null);
  const [postToDelete, setPostToDelete] = useState<number | null>(null);
  const [videoPreview, setVideoPreview] = useState<{
    url: string | null;
    postId: number;
    sourceType?: 'S3_PERMANENT' | 'S3_CACHED' | 'GDRIVE';
    sourceRef?: PostMedia['sourceRef'];
  } | null>(null);
  const [carouselMedia, setCarouselMedia] = useState<{ media: PostMedia[]; index: number; postId: number } | null>(null);
  const [variationCreator, setVariationCreator] = useState<{
    isOpen: boolean;
    preSelectedPostId: number | null;
  }>({
    isOpen: false,
    preSelectedPostId: null,
  });
  const [expandedCaptions, setExpandedCaptions] = useState<Set<number>>(new Set());
  const [overflowingCaptions, setOverflowingCaptions] = useState<Set<number>>(new Set());
  const captionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const isMobile = useMediaQuery('(max-width: 768px)');

  /**
   * Toggle caption expansion for a post
   */
  const toggleCaptionExpansion = (postId: number) => {
    setExpandedCaptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  /**
   * Workflow status configuration (for approval workflow)
   */
  const WORKFLOW_STATUS_CONFIG = {
    UNSCHEDULED: {
      label: t('workflow_unscheduled'),
      color: 'bg-gray-100 text-gray-700',
      icon: ICONS.FILE,
    },
    PENDING: {
      label: t('workflow_pending_review'),
      color: 'bg-yellow-100 text-yellow-700',
      icon: ICONS.REVIEW,
    },
    CHANGES_REQUESTED: {
      label: t('workflow_changes_requested'),
      color: 'bg-red-100 text-red-700',
      icon: ICONS.CAUTION,
    },
    APPROVED: {
      label: t('workflow_released'),
      color: 'bg-green-100 text-green-700',
      icon: ICONS.CHECK,
    },
  } as const;

  /**
   * Handle keyboard navigation for carousel
   */
  useEffect(() => {
    if (!carouselMedia) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        setCarouselMedia(null);
      } else if (e.key === 'ArrowLeft' && carouselMedia.index > 0) {
        setCarouselMedia(prev => prev ? { ...prev, index: prev.index - 1 } : null);
      } else if (e.key === 'ArrowRight' && carouselMedia.index < carouselMedia.media.length - 1) {
        setCarouselMedia(prev => prev ? { ...prev, index: prev.index + 1 } : null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [carouselMedia]);

  /**
   * Check for caption overflow to determine when to show "show more" button
   * Note: We intentionally do NOT include expandedCaptions in dependencies.
   * We only want to check overflow on initial render and window resize, NOT when captions expand/collapse.
   */
  useEffect(() => {
    const checkOverflow = () => {
      setOverflowingCaptions(prev => {
        const newOverflowing = new Set<number>(prev);

        captionRefs.current.forEach((el, postId) => {
          // Only check captions that are NOT expanded
          // Expanded captions have maxHeight: 500px so they won't show overflow
          // We preserve their overflow state to keep the button visible
          if (!expandedCaptions.has(postId)) {
            if (el && el.scrollHeight > el.clientHeight) {
              newOverflowing.add(postId);
            } else {
              newOverflowing.delete(postId);
            }
          }
          // If caption IS expanded, preserve its previous overflow state (don't modify)
        });

        return newOverflowing;
      });
    };

    // Small delay to ensure DOM has rendered
    const timeoutId = setTimeout(checkOverflow, 50);

    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts]); // Only re-check when posts change, NOT when expandedCaptions changes

  /**
   * Calculate word count from text
   */
  const getWordCount = (text: string): number => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  // Row actions menu (the portaled table menu AND the inline card menu on mobile): Escape closes it and returns
  // focus to its trigger. The portaled one is positioned from the trigger rect, so it also closes on scroll.
  useEffect(() => {
    if (openMenuPostId === null) return;
    const close = () => {
      setOpenMenuPostId(null);
      setMenuPosition(null);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape' || ev.defaultPrevented) return;
      close();
      menuTriggerRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    if (menuPosition !== null) window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
    };
  }, [openMenuPostId, menuPosition]);

  /**
   * Handle delete with confirmation
   */
  const handleDelete = (postId: number) => {
    setOpenMenuPostId(null);
    setMenuPosition(null);
    setPostToDelete(postId);
  };

  /**
   * Confirm delete action
   */
  const handleConfirmDelete = () => {
    if (postToDelete !== null) {
      onDelete(postToDelete);
    }
    setPostToDelete(null);
  };

  /**
   * Check if post is selected
   */
  const isSelected = (postId: number): boolean => {
    return selectedPosts.includes(postId);
  };

  /**
   * Check if all posts are selected
   */
  const allSelected = posts.length > 0 && selectedPosts.length === posts.length;

  /**
   * Handle adding a new variation
   */
  const handleAddVariation = (post: Post) => {
    setVariationCreator({
      isOpen: true,
      preSelectedPostId: post.id,
    });
  };

  // Sticky offset for left-hand row columns: sits just below the sticky <thead>
  // (same offset as thead + ~2.75rem header height) so media/actions stay pinned
  // to the top of tall (expanded) rows while the page scrolls.
  const stickyColStyle: CSSProperties = {
    top: selectedPosts.length > 0
      ? 'calc(4.1rem + 59px + 61px + 2.75rem)'
      : 'calc(4.1rem + 59px + 2.75rem)',
  };

  // Loading skeleton - table layout matching actual table structure
  if (isLoading && posts.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="w-10 p-2 md:p-3"></th>
                <th className="w-10 p-2 md:p-3"></th>
                <th className="hidden md:table-cell w-20 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">{t('media')}</th>
                <th className="p-2 md:p-3 text-left text-xs font-semibold text-gray-600">{t('caption')}</th>
                <th className="hidden lg:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">{t('tags')}</th>
                <th className="hidden md:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">{t('status')}</th>
                {workflowEnabled && (
                  <th className="hidden md:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">{t('workflow')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="p-2 md:p-3 align-top">
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="p-2 md:p-3 align-top">
                    <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="hidden md:table-cell p-2 md:p-3 align-top">
                    <div className="w-14 h-14 bg-gray-200 rounded-lg animate-pulse"></div>
                  </td>
                  <td className="p-2 md:p-3 align-top">
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                      <div className="flex gap-2 mt-1">
                        <div className="h-5 bg-gray-200 rounded w-16 animate-pulse"></div>
                        <div className="h-5 bg-gray-200 rounded w-14 animate-pulse"></div>
                      </div>
                    </div>
                  </td>
                  <td className="hidden lg:table-cell p-2 md:p-3 align-top">
                    <div className="flex gap-1">
                      <div className="h-6 bg-gray-200 rounded w-14 animate-pulse"></div>
                      <div className="h-6 bg-gray-200 rounded w-12 animate-pulse"></div>
                    </div>
                  </td>
                  <td className="hidden md:table-cell p-2 md:p-3 align-top">
                    <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
                  </td>
                  {workflowEnabled && (
                    <td className="hidden md:table-cell p-2 md:p-3 align-top">
                      <div className="h-6 bg-gray-200 rounded w-20 animate-pulse"></div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Empty state
  if (posts.length === 0 && !isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-card p-12 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <Icon name={ICONS.LIST} size="lg" color="text-gray-400" ariaHidden />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{t('no_posts_found')}</h3>
        <p className="text-gray-600">
          {t('try_adjusting_filters')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-card">
      {isMobile ? (
        // Mobile card view
        <div className="grid grid-cols-1 gap-4 p-4">
          {posts.map((post) => {
            const selected = isSelected(post.id);
            const firstMedia = post.postMedia && post.postMedia.length > 0 ? post.postMedia[0] : null;
            const statusFlags = getListStatusFlags(post);
            const mediaCount = post.postMedia?.length || 0;
            const hashtags = extractHashtags(post.caption);
            const charCount = post.caption.length;

            return (
              <div
                key={post.id}
                className={`border rounded-lg p-4 space-y-3 group/card ${
                  selected ? 'border-bs-secondary bg-light-teal-gray/25' : 'border-gray-200'
                }`}
              >
                {/* Row 1: Info bar — [Checkbox] [Status badge] ··· [Actions] */}
                <div className="flex items-center gap-2">
                  {/* Left: Checkbox + content status badge */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Checkbox
                      checked={selected}
                      onCheckedChange={() => onSelect(post.id)}
                      aria-label={t('select_post', { id: post.id })}
                    />
                    {/* eslint-disable i18next/no-literal-string */}
                    {post.contentStatus === 'DRAFT' ? (
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded whitespace-nowrap flex items-center gap-1">
                        ✏️ {t('draft', { defaultValue: 'Draft' })}
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded whitespace-nowrap flex items-center gap-1">
                        🚀 {t('ready_to_shoot', { defaultValue: 'Ready' })}
                      </span>
                    )}
                    {/* eslint-enable i18next/no-literal-string */}
                  </div>

                  {/* Right: actions button */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <div className="relative">
                      <Tooltip content={t('actions')}>
                        <button
                          type="button"
                          onClick={(e) => {
                            if (openMenuPostId === post.id) {
                              setOpenMenuPostId(null);
                            } else {
                              menuTriggerRef.current = e.currentTarget;
                              const rect = e.currentTarget.getBoundingClientRect();
                              // Below lg the fixed bottom nav (~64px + safe area) covers the lower edge.
                              const reserved = window.innerWidth < 1024 ? getBottomNavReservePx() : 0;
                              const below = window.innerHeight - reserved - rect.bottom;
                              setCardMenuUp(below < CARD_MENU_MIN_ROOM_PX && rect.top > below);
                              setOpenMenuPostId(post.id);
                            }
                          }}
                          className="relative w-7 h-7 max-sm:after:absolute max-sm:after:-inset-[10px] max-sm:after:content-[''] flex items-center justify-center bg-soft-mint hover:bg-soft-mint/70 rounded-md transition-colors"
                          aria-label={t('post_actions')}
                          aria-expanded={openMenuPostId === post.id}
                        >
                          <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" color="text-dark-teal" ariaHidden />
                        </button>
                      </Tooltip>
                      {/* Inline dropdown — anchored to button */}
                      {openMenuPostId === post.id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setOpenMenuPostId(null)}
                          />
                          <div
                            className={`absolute right-0 z-50 max-h-[min(60vh,calc(100dvh-10rem))] overflow-y-auto ${ROW_ACTION_MENU_SURFACE} ${
                              cardMenuUp ? 'bottom-full mb-1' : 'top-full mt-1'
                            }`}
                          >
                            {(!canEditPost || canEditPost(post)) && (
                              <button
                                onClick={() => { setOpenMenuPostId(null); onEdit(post.id); }}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name={ICONS.EDIT} size="sm" ariaHidden />
                                {t('edit')}
                              </button>
                            )}
                            {(!canDuplicatePost || canDuplicatePost(post)) && (
                              <button
                                onClick={() => { setOpenMenuPostId(null); onDuplicate(post.id); }}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name={ICONS.COPY} size="sm" ariaHidden />
                                {t('duplicate')}
                              </button>
                            )}
                            {onScheduleNow && post.contentStatus === 'READY' && (!canSchedulePost || canSchedulePost(post)) && (
                              <button
                                onClick={() => { setOpenMenuPostId(null); onScheduleNow(post.id); }}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name="rocket1" size="sm" ariaHidden />
                                {t('shoot_now')}
                              </button>
                            )}
                            {onMarkReady && (!canMarkReady || canMarkReady(post)) && post.contentStatus !== 'READY' && (
                              <button
                                onClick={() => { setOpenMenuPostId(null); onMarkReady(post.id); }}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name={ICONS.CHECK_CIRCLE} size="sm" ariaHidden />
                                {t('mark_ready')}
                              </button>
                            )}
                            {onMarkDraft && (!canMarkDraft || canMarkDraft(post)) && post.contentStatus !== 'DRAFT' && (
                              <button
                                onClick={() => { setOpenMenuPostId(null); onMarkDraft(post.id); }}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name={ICONS.DRAFT} size="sm" ariaHidden />
                                {t('mark_draft')}
                              </button>
                            )}
                            {workflowEnabled && onSendForReview && (!canSendForReview || canSendForReview(post)) && (
                              <button
                                onClick={() => { setOpenMenuPostId(null); onSendForReview(post.id); }}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name={ICONS.PAPER_PLANE} size="sm" ariaHidden />
                                {t('send_for_review')}
                              </button>
                            )}
                            {/* Conversation is available wherever the release workflow applies. */}
                            {workflowEnabled && (
                              <>
                                <div className={ROW_ACTION_MENU_DIVIDER} />
                                <button
                                  onClick={() => {
                                    setOpenMenuPostId(null);
                                    setMenuPosition(null);
                                    openReleaseDrawer({ postId: post.id, variationId: null, tab: 'comments', source: 'list' });
                                  }}
                                  className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                                >
                                  <Icon name={ICONS.CHAT} size="sm" ariaHidden />
                                  {t('common:release_open_conversation')}
                                </button>
                              </>
                            )}
                            {(!canDeletePost || canDeletePost(post)) && (
                              <>
                                <div className={ROW_ACTION_MENU_DIVIDER} />
                                <button
                                  onClick={() => { setOpenMenuPostId(null); handleDelete(post.id); }}
                                  className={ROW_ACTION_MENU_ITEM + ' text-red-600 hover:bg-red-50 focus-visible:bg-red-50'}
                                >
                                  <Icon name={ICONS.TRASH} size="sm" ariaHidden />
                                  {t('delete')}
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 2: Thumbnail (1/3) + Caption (2/3) */}
                <div className="flex gap-3 items-start group/card">
                  {/* Thumbnail — 1/3 width */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (mediaCount > 1 && post.postMedia) {
                        setCarouselMedia({ media: post.postMedia, index: 0, postId: post.id });
                      } else if (firstMedia?.type === 'VIDEO' && (firstMedia.originalUrl || firstMedia.sourceRef?.driveFileId)) {
                        setVideoPreview({ url: firstMedia.originalUrl, postId: post.id, sourceType: firstMedia.sourceType, sourceRef: firstMedia.sourceRef });
                      } else if ((firstMedia?.type === 'PDF' || firstMedia?.type === 'DOCUMENT') && firstMedia.originalUrl) {
                        setCarouselMedia({ media: post.postMedia!, index: 0, postId: post.id });
                      } else if (firstMedia?.type === 'IMAGE' && post.postMedia) {
                        setCarouselMedia({ media: post.postMedia, index: 0, postId: post.id });
                      }
                    }}
                    className="cursor-zoom-in relative w-1/3 flex-shrink-0 aspect-square bg-gray-100 rounded-lg overflow-hidden group"
                    aria-label={mediaCount > 1 ? `View all ${mediaCount} media items` : 'View media'}
                  >
                    {firstMedia ? (
                      <>
                        {(firstMedia.type === 'PDF' || firstMedia.type === 'DOCUMENT') && !firstMedia.thumbnailUrl ? (
                          <FileThumbnailPlaceholder className="w-full h-full" iconSize="lg" />
                        ) : (
                          <RetryImage
                            src={
                              firstMedia.type === 'PDF' || firstMedia.type === 'DOCUMENT'
                                ? firstMedia.thumbnailUrl || undefined
                                : firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined
                            }
                            alt=""
                            className={`w-full h-full ${firstMedia.type === 'VIDEO' || firstMedia.type === 'PDF' || firstMedia.type === 'DOCUMENT' ? 'object-contain bg-gray-900' : 'object-cover'}`}
                          />
                        )}
                        {/* Play/view indicator — always visible on mobile, hover-revealed on desktop */}
                        {(mediaCount > 1 || firstMedia.type === 'VIDEO' || firstMedia.type === 'PDF' || firstMedia.type === 'DOCUMENT') && (
                          <div className="absolute inset-0 bg-black/0 lg:group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                            <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                              {firstMedia.type === 'PDF' || firstMedia.type === 'DOCUMENT' ? (
                                <Icon name={ICONS.FILE} size="sm" color="text-gray-800" ariaHidden />
                              ) : (
                                <span className="text-gray-800 text-sm ml-0.5">▶</span>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Media count badge */}
                        {mediaCount > 1 && (
                          <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                            +{mediaCount - 1}
                          </div>
                        )}
                      </>
                    ) : (
                      <TextPostTile text={post.caption} emptyLabel={t('common:approval_no_content')} className="rounded-lg" />
                    )}
                  </button>

                  {/* Caption — 2/3 width */}
                  <div className="flex-1 min-w-0">
                    <div
                      ref={(el) => {
                        if (el) captionRefs.current.set(post.id, el);
                        else captionRefs.current.delete(post.id);
                      }}
                      className="overflow-hidden transition-all duration-300 ease-in-out"
                      style={{
                        maxHeight: expandedCaptions.has(post.id) ? '500px' : '4.8em',
                      }}
                    >
                      {post.caption ? (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap text-gray-800">
                          {post.caption}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">{t('no_caption')}</p>
                      )}
                    </div>
                    {/* Show more/less — mobile: text link with chevron; desktop: hover pill */}
                    {(overflowingCaptions.has(post.id) || expandedCaptions.has(post.id)) && (
                      <>
                        {/* Mobile: full-width subtle text link */}
                        <button
                          onClick={() => toggleCaptionExpansion(post.id)}
                          className="lg:hidden inline-flex items-center gap-1 mt-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                        >
                          {expandedCaptions.has(post.id) ? t('show_less') : t('show_more')}
                          <span
                            className="inline-block transition-transform duration-300"
                            style={{ transform: expandedCaptions.has(post.id) ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          >
                            ↓
                          </span>
                        </button>
                        {/* Desktop: hover-revealed pill (original) */}
                        <button
                          onClick={() => toggleCaptionExpansion(post.id)}
                          className={`hidden lg:inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-soft-mint text-dark-teal text-xs font-medium rounded hover:bg-soft-mint/70 transition-all duration-200 whitespace-nowrap ${
                            expandedCaptions.has(post.id) ? 'opacity-100' : 'opacity-0 group-hover/card:opacity-100'
                          }`}
                        >
                          {expandedCaptions.has(post.id) ? t('show_less') : t('show_more')}
                          <span
                            className="inline-block transition-transform duration-300"
                            style={{ transform: expandedCaptions.has(post.id) ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          >
                            ↓
                          </span>
                        </button>
                      </>
                    )}

                  </div>
                </div>

                {/* Row 3: Meta info — date, media type, hashtags, instances, tags, edit */}
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                    {formatRelativeDate(post.createdAt)}
                  </span>
                  {charCount === 0 && (
                    <span className="px-1.5 py-0.5 bg-gray-50 text-gray-400 rounded italic">{t('no_caption')}</span>
                  )}
                  <MediaTypeBadge mediaType={firstMedia?.type || 'IMAGE'} mediaCount={mediaCount} variant="inline" />
                  {hashtags.length > 0 && (
                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{hashtags.length} #</span>
                  )}
                  {/* Instance summary badges */}
                  {statusFlags.showMapped ? (
                    <Tooltip content={t('tooltip_status_mapped')}>
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">📅 {post.instancesSummary?.mapped}</span>
                    </Tooltip>
                  ) : null}
                  {post.instancesSummary?.scheduled && post.instancesSummary.scheduled > 0 ? (
                    <Tooltip content={t('status_scheduled')}>
                      <span className="px-1.5 py-0.5 bg-soft-mint text-dark-teal rounded">🚀 {post.instancesSummary.scheduled}</span>
                    </Tooltip>
                  ) : null}
                  {/* First-class PUBLISHING bucket (Stage 2c-i / #182 B1) — was
                      previously counted in `total` but had no render branch,
                      leaving a blank cell for publishing-only occurrences. */}
                  {post.instancesSummary?.publishing && post.instancesSummary.publishing > 0 ? (
                    <Tooltip content={t('status_publishing')}>
                      <span
                        className={`px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${OCCURRENCE_STATUS_BADGES.PUBLISHING.bgColor} ${OCCURRENCE_STATUS_BADGES.PUBLISHING.color}`}
                      >
                        <Icon name={OCCURRENCE_STATUS_BADGES.PUBLISHING.icon} size="xs" ariaHidden />
                        {post.instancesSummary.publishing}
                      </span>
                    </Tooltip>
                  ) : null}
                  {statusFlags.showPublished ? (
                    <Tooltip content={t('status_published')}>
                      <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded">✅ {post.instancesSummary?.published || ''}</span>
                    </Tooltip>
                  ) : null}
                  {post.instancesSummary?.partial && post.instancesSummary.partial > 0 ? (
                    <Tooltip content={t('status_partial')}>
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">🟡 {post.instancesSummary.partial}</span>
                    </Tooltip>
                  ) : null}
                  {post.instancesSummary?.failed && post.instancesSummary.failed > 0 ? (
                    <Tooltip content={t('status_failed')}>
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded">⚠️ {post.instancesSummary.failed}</span>
                    </Tooltip>
                  ) : null}
                  {post.instancesSummary?.missed && post.instancesSummary.missed > 0 ? (
                    <Tooltip content={t('status_missed')}>
                      <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">⌛ {post.instancesSummary.missed}</span>
                    </Tooltip>
                  ) : null}
                  {/* First-class CANCELLED bucket (Stage 2c-i / #182 B1) — was
                      previously counted in `total` but had no render branch,
                      leaving a blank cell for cancelled-only occurrences. */}
                  {post.instancesSummary?.cancelled && post.instancesSummary.cancelled > 0 ? (
                    <Tooltip content={t('status_cancelled')}>
                      <span
                        className={`px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${OCCURRENCE_STATUS_BADGES.CANCELLED.bgColor} ${OCCURRENCE_STATUS_BADGES.CANCELLED.color}`}
                      >
                        <Icon name={OCCURRENCE_STATUS_BADGES.CANCELLED.icon} size="xs" ariaHidden />
                        {post.instancesSummary.cancelled}
                      </span>
                    </Tooltip>
                  ) : null}
                  {(!post.instancesSummary || post.instancesSummary.total === 0) && (
                    <Tooltip content={t('unmapped')}>
                      <span className="text-gray-400">☁️</span>
                    </Tooltip>
                  )}
                  {/* Tag badges */}
                  {post.labels && post.labels.length > 0 && (
                    <>
                      {post.labels.slice(0, 2).map((label) => (
                        <button
                          key={label}
                          onClick={() => onFilterByTag && onFilterByTag(label)}
                          className="px-1.5 py-0.5 rounded bg-soft-mint text-dark-teal hover:bg-soft-mint/70 transition-colors whitespace-nowrap"
                          aria-label={t('filter_by_label', { label })}
                        >
                          {label}
                        </button>
                      ))}
                      {post.labels.length > 2 && (
                        <span className="text-gray-400">+{post.labels.length - 2}</span>
                      )}
                    </>
                  )}
                  {/* Edit button */}
                  {(!canEditPost || canEditPost(post)) && (
                    <Tooltip content={t('edit')}>
                      <button
                        type="button"
                        onClick={() => onEdit(post.id)}
                        className="inline-flex items-center justify-center h-[18px] w-[18px] bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors"
                      >
                        <Icon name={ICONS.EDIT} size="xs" ariaHidden />
                      </button>
                    </Tooltip>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      ) : (
        // Desktop table view
          <table className="w-full">
          {/* Table Header */}
          <thead className={`sticky z-10 bg-gray-50 border-b border-gray-200 ${selectedPosts.length > 0 ? 'top-[calc(4.1rem+59px+61px)]' : 'top-[calc(4.1rem+59px)]'}`}>
            <tr>
              {/* Actions */}
              <th className="w-10 p-2 md:p-3">
              </th>
              {/* Checkbox */}
              <th className="w-10 p-2 md:p-3">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={onSelectAll}
                  aria-label={t('select_all_posts')}
                />
              </th>
              {/* Media */}
              <th className="hidden md:table-cell w-20 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                {t('media')}
              </th>
              {/* Caption */}
              <th className="p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                {t('caption')}
              </th>
              {/* Tags */}
              <th className="hidden lg:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                {t('tags')}
              </th>
              {/* Versions - TODO: Add back later
              <th className="hidden lg:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                {t('versions')}
              </th>
              */}
              {/* Status (Aggregated Instances) */}
              <th className="hidden md:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                {t('status')}
              </th>
              {/* Workflow Status (conditional) */}
              {workflowEnabled && (
                <th className="hidden md:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                  {t('workflow')}
                </th>
              )}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {posts.map((post) => {
              const selected = isSelected(post.id);
              const firstMedia = post.postMedia && post.postMedia.length > 0 ? post.postMedia[0] : null;
              const statusFlags = getListStatusFlags(post);
              const hashtags = extractHashtags(post.caption);
              const charCount = post.caption.length;
              const wordCount = getWordCount(post.caption);
              const workflowStatus = post.workflowStatus || 'UNSCHEDULED';
              const statusConfig = WORKFLOW_STATUS_CONFIG[workflowStatus] || WORKFLOW_STATUS_CONFIG.UNSCHEDULED;

              return (
                <tr
                  key={post.id}
                  className={`border-b border-gray-100 transition-colors group/row ${
                    selected ? 'bg-light-teal-gray/25' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Actions Dropdown */}
                  <td className="p-2 md:p-3 align-top text-center">
                    <div className="sticky flex items-center justify-center h-14" style={stickyColStyle}>
                      <Tooltip content={t('actions')}>
                        <button
                          type="button"
                          onClick={(e) => {
                            if (openMenuPostId === post.id) {
                              setOpenMenuPostId(null);
                              setMenuPosition(null);
                            } else {
                              menuTriggerRef.current = e.currentTarget;
                              const rect = e.currentTarget.getBoundingClientRect();
                              const spaceBelow = window.innerHeight - rect.bottom;

                              // Horizontal positioning - open to the right
                              const left = rect.left;

                              // Vertical: if enough space below, use top; otherwise use bottom
                              const reserved = window.innerWidth < 1024 ? getBottomNavReservePx() : 0;
                              if (spaceBelow - reserved >= 280) {
                                setMenuPosition({ top: rect.bottom + 4, left });
                              } else {
                                // Use bottom so menu sticks to button regardless of menu height
                                setMenuPosition({ bottom: window.innerHeight - rect.top + 4, left });
                              }
                              setOpenMenuPostId(post.id);
                            }
                          }}
                          className="relative p-1.5 max-sm:after:absolute max-sm:after:-inset-[8px] max-sm:after:content-[''] hover:bg-gray-100 rounded-lg transition-colors"
                          aria-label={t('post_actions')}
                          aria-expanded={openMenuPostId === post.id}
                        >
                          <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" color="text-gray-600" ariaHidden />
                        </button>
                      </Tooltip>

                      {/* Dropdown Menu */}
                      {openMenuPostId === post.id && menuPosition && createPortal(
                        <>
                          {/* Backdrop */}
                          <div
                            className="fixed inset-0 z-[69]"
                            onClick={() => {
                              setOpenMenuPostId(null);
                              setMenuPosition(null);
                            }}
                          />

                          {/* Menu */}
                          <div
                            className={`fixed z-[70] max-h-[calc(100vh-2rem)] overflow-y-auto ${ROW_ACTION_MENU_SURFACE}`}
                            style={{
                              left: menuPosition.left,
                              ...(menuPosition.top !== undefined ? { top: menuPosition.top } : { bottom: menuPosition.bottom }),
                            }}
                          >
                            {(!canEditPost || canEditPost(post)) && (
                              <button
                                onClick={() => {
                                  setOpenMenuPostId(null);
                                  setMenuPosition(null);
                                  onEdit(post.id);
                                }}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name={ICONS.EDIT} size="sm" ariaHidden />
                                {t('edit')}
                              </button>
                            )}
                            {(!canDuplicatePost || canDuplicatePost(post)) && (
                              <button
                                onClick={() => {
                                  setOpenMenuPostId(null);
                                  setMenuPosition(null);
                                  onDuplicate(post.id);
                                }}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name={ICONS.COPY} size="sm" ariaHidden />
                                {t('duplicate')}
                              </button>
                            )}
                            {canCreateVariation && (
                            <button
                              onClick={() => {
                                setOpenMenuPostId(null);
                                setMenuPosition(null);
                                handleAddVariation(post);
                              }}
                              className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                            >
                              <Icon name={ICONS.PLUS_CIRCLE} size="sm" ariaHidden />
                              {t('add_variation')}
                            </button>
                            )}
                            {/* Schedule Now - show for READY posts when can schedule */}
                            {onScheduleNow && post.contentStatus === 'READY' && (!canSchedulePost || canSchedulePost(post)) && (
                              <button
                                onClick={() => {
                                  setOpenMenuPostId(null);
                                  setMenuPosition(null);
                                  onScheduleNow(post.id);
                                }}
                                className={ROW_ACTION_MENU_ITEM + ' text-dark-teal hover:bg-soft-mint focus-visible:bg-soft-mint'}
                              >
                                <Icon name="rocket1" size="sm" ariaHidden />
                                {t('shoot_now')}
                              </button>
                            )}
                            {/* Mark Ready/Draft toggle */}
                            {post.contentStatus === 'DRAFT' && onMarkReady && (!canMarkReady || canMarkReady(post)) ? (
                              <button
                                onClick={() => {
                                  setOpenMenuPostId(null);
                                  setMenuPosition(null);
                                  onMarkReady(post.id);
                                }}
                                className={ROW_ACTION_MENU_ITEM + ' text-green-600 hover:bg-green-50 focus-visible:bg-green-50'}
                              >
                                <Icon name={ICONS.CHECK} size="sm" ariaHidden />
                                {t('mark_ready')}
                              </button>
                            ) : post.contentStatus === 'READY' && onMarkDraft && (!canMarkDraft || canMarkDraft(post)) ? (
                              <button
                                onClick={() => {
                                  setOpenMenuPostId(null);
                                  setMenuPosition(null);
                                  onMarkDraft(post.id);
                                }}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name={ICONS.FILE} size="sm" ariaHidden />
                                {t('mark_draft')}
                              </button>
                            ) : null}
                            {workflowEnabled && onSendForReview && (!canSendForReview || canSendForReview(post)) && (
                              <button
                                onClick={() => {
                                  setOpenMenuPostId(null);
                                  setMenuPosition(null);
                                  onSendForReview(post.id);
                                }}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name={ICONS.PAPER_PLANE} size="sm" ariaHidden />
                                {t('send_for_review')}
                              </button>
                            )}
                            {/* Conversation is available wherever the release workflow applies. */}
                            {workflowEnabled && (
                              <>
                                <div className={ROW_ACTION_MENU_DIVIDER} />
                                <button
                                  onClick={() => {
                                    setOpenMenuPostId(null);
                                    setMenuPosition(null);
                                    openReleaseDrawer({ postId: post.id, variationId: null, tab: 'comments', source: 'list' });
                                  }}
                                  className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                                >
                                  <Icon name={ICONS.CHAT} size="sm" ariaHidden />
                                  {t('common:release_open_conversation')}
                                </button>
                              </>
                            )}
                            {(!canDeletePost || canDeletePost(post)) && (
                              <>
                                <div className={ROW_ACTION_MENU_DIVIDER} />
                                <button
                                  onClick={() => handleDelete(post.id)}
                                  className={ROW_ACTION_MENU_ITEM + ' text-red-600 hover:bg-red-50 focus-visible:bg-red-50'}
                                >
                                  <Icon name={ICONS.TRASH} size="sm" ariaHidden />
                                  {t('delete')}
                                </button>
                              </>
                            )}
                          </div>
                        </>,
                        document.body
                        )}
                    </div>
                  </td>

                  {/* Checkbox */}
                  <td className="p-2 md:p-3 align-top">
                    <div className="sticky flex items-center h-14" style={stickyColStyle}>
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => onSelect(post.id)}
                        aria-label={t('select_post', { id: post.id })}
                      />
                    </div>
                  </td>

                  {/* Media Thumbnail */}
                  <td className="hidden md:table-cell p-2 md:p-3 align-top">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const mediaCount = post.postMedia?.length || 0;
                        if (mediaCount > 1 && post.postMedia) {
                          setCarouselMedia({ media: post.postMedia, index: 0, postId: post.id });
                        } else if (firstMedia?.type === 'VIDEO' && (firstMedia.originalUrl || firstMedia.sourceRef?.driveFileId)) {
                          setVideoPreview({ url: firstMedia.originalUrl, postId: post.id, sourceType: firstMedia.sourceType, sourceRef: firstMedia.sourceRef });
                        } else if ((firstMedia?.type === 'PDF' || firstMedia?.type === 'DOCUMENT') && firstMedia.originalUrl) {
                          setCarouselMedia({ media: post.postMedia!, index: 0, postId: post.id });
                        } else if (firstMedia?.type === 'IMAGE' && post.postMedia) {
                          // Single image: reuse the media viewer modal for zoom
                          setCarouselMedia({ media: post.postMedia, index: 0, postId: post.id });
                        } else {
                          onSelect(post.id);
                        }
                      }}
                      className={`sticky w-14 h-14 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 group ${firstMedia ? 'cursor-zoom-in' : 'cursor-pointer'}`}
                      style={stickyColStyle}
                      aria-label={post.postMedia && post.postMedia.length > 1 ? `${t('common:view_media')} (${post.postMedia.length})` : t('common:view_media')}
                    >
                      {firstMedia ? (
                        <>
                          {(firstMedia.type === 'PDF' || firstMedia.type === 'DOCUMENT') && !firstMedia.thumbnailUrl ? (
                            <FileThumbnailPlaceholder className="w-full h-full" iconSize="md" />
                          ) : (
                            <RetryImage
                              src={
                                firstMedia.type === 'PDF' || firstMedia.type === 'DOCUMENT'
                                  ? firstMedia.thumbnailUrl || undefined
                                  : firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined
                              }
                              alt=""
                              className={`w-full h-full ${firstMedia.type === 'VIDEO' || firstMedia.type === 'PDF' || firstMedia.type === 'DOCUMENT' ? 'object-contain bg-gray-900' : 'object-cover'}`}
                            />
                          )}

                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />

                          {/* Zoom affordance (single image) */}
                          {firstMedia.type === 'IMAGE' && !(post.postMedia && post.postMedia.length > 1) && (
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:hidden transition-opacity pointer-events-none">
                              <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                                <Icon name={ICONS.SEARCH} size="sm" color="text-gray-800" ariaHidden />
                              </div>
                            </div>
                          )}

                          {/* Play/View overlay on hover */}
                          {(firstMedia.type === 'VIDEO' || firstMedia.type === 'PDF' || firstMedia.type === 'DOCUMENT' || (post.postMedia && post.postMedia.length > 1)) && (
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
                              <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                                {firstMedia.type === 'PDF' || firstMedia.type === 'DOCUMENT' ? (
                                  <Icon name={ICONS.FILE} size="sm" color="text-gray-800" ariaHidden />
                                ) : (
                                  <span className="text-gray-800 text-sm ml-0.5">▶</span>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <TextPostTile text={post.caption} emptyLabel={t('common:approval_no_content')} className="rounded-lg" />
                      )}
                    </button>
                  </td>

                  {/* Caption with badges */}
                  <td className="p-2 md:p-3 align-top">
                    <div className="space-y-1">
                      <div
                        ref={(el) => {
                          if (el) captionRefs.current.set(post.id, el);
                          else captionRefs.current.delete(post.id);
                        }}
                        className="overflow-hidden transition-all duration-300 ease-in-out"
                        style={{
                          maxHeight: expandedCaptions.has(post.id) ? '500px' : '2.6em',
                        }}
                      >
                        <p className="text-xs md:text-sm text-gray-700 whitespace-pre-wrap">
                          {post.caption}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="whitespace-nowrap rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          {formatRelativeDate(post.createdAt)}
                        </span>
                        {charCount > 0 ? (
                          <>
                            {/* Character count badge */}
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                              {t('x_chars', { count: charCount })}
                            </span>
                            {/* Word count badge */}
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                              {t('x_words', { count: wordCount })}
                            </span>
                          </>
                        ) : (
                          /* No caption badge */
                          <span className="px-2 py-0.5 bg-gray-50 text-gray-400 text-xs font-medium rounded italic">
                            {t('no_caption')}
                          </span>
                        )}
                        {/* Media type badge */}
                        <MediaTypeBadge mediaType={firstMedia?.type || 'IMAGE'} mediaCount={post.postMedia?.length || 0} variant="inline" />
                        {/* Content status badge (Draft/Ready) - evergreen, independent of publish state */}
                        {/* eslint-disable i18next/no-literal-string */}
                        {post.contentStatus === 'DRAFT' && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded flex items-center gap-1">
                            ✏️ {t('draft', { defaultValue: 'Draft' })}
                          </span>
                        )}
                        {/* eslint-enable i18next/no-literal-string */}
                        {post.contentStatus === 'READY' && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded flex items-center gap-1">
                            🚀 {t('ready_to_shoot', { defaultValue: 'Ready to Shoot' })}
                          </span>
                        )}
                        {/* Hashtag count badge */}
                        {hashtags.length > 0 && (
                          <span className="px-2 py-0.5 bg-off-white text-bs-gray-stone text-xs font-medium rounded">
                            #{hashtags.length}
                          </span>
                        )}
                        {/* Show more/less badge - at the end, only if caption actually overflows */}
                        {(overflowingCaptions.has(post.id) || expandedCaptions.has(post.id)) && (
                          <button
                            onClick={() => toggleCaptionExpansion(post.id)}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 bg-soft-mint text-dark-teal text-xs font-medium rounded hover:bg-soft-mint/70 transition-all duration-200 ${
                              expandedCaptions.has(post.id) ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100'
                            }`}
                          >
                            {expandedCaptions.has(post.id) ? t('show_less') : t('show_more')}
                            <span
                              className="inline-block transition-transform duration-300"
                              style={{ transform: expandedCaptions.has(post.id) ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            >
                              ↓
                            </span>
                          </button>
                        )}
                        {/* Edit badge - visible on hover */}
                        {(!canEditPost || canEditPost(post)) && (
                          <Tooltip content={t('edit')}>
                            <button
                              onClick={() => onEdit(post.id)}
                              className="inline-flex items-center justify-center h-[20px] w-[20px] bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-all duration-200 opacity-0 group-hover/row:opacity-100"
                            >
                              <Icon name={ICONS.EDIT} size="xs" ariaHidden />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Tags (Labels) */}
                  <td className="hidden lg:table-cell p-2 md:p-3">
                    <div className="flex flex-wrap gap-1">
                      {post.labels && post.labels.length > 0 ? (
                        <>
                          {post.labels.slice(0, 3).map((label) => (
                            <Tooltip key={label} content={t('filter_by_label', { label })}>
                              <button
                                onClick={() => onFilterByTag && onFilterByTag(label)}
                                className="px-2 py-0.5 text-xs rounded-md bg-soft-mint text-dark-teal hover:bg-soft-mint/70 transition-colors"
                              >
                                {label}
                              </button>
                            </Tooltip>
                          ))}
                          {post.labels.length > 3 && (
                            <span className="px-2 py-0.5 text-xs text-gray-400">
                              +{post.labels.length - 3}
                            </span>
                          )}
                        </>
                      ) : null}
                    </div>
                  </td>

                  {/* Versions - TODO: Add back later
                  <td className="hidden lg:table-cell p-2 md:p-3">
                    <div className="flex items-center gap-1.5 group">
                      {post.variationsCount && post.variationsCount > 0 ? (
                        <>
                          <Select
                            size="sm"
                            value={selectedVariations[post.id] || 'original'}
                            onValueChange={(value) => {
                              setSelectedVariations(prev => ({ ...prev, [post.id]: value }));
                              handleSelectVariation(post.id, value);
                            }}
                            options={[
                              { value: 'original', label: t('original') },
                              ...(post.variations?.map((v) => ({
                                value: String(v.id),
                                label: v.title,
                              })) || []),
                            ]}
                            placeholder={t('select_version')}
                            className="min-w-[110px]"
                          />
                          <span className="text-xs text-gray-500">
                            ({post.variationsCount})
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">{t('no_versions')}</span>
                      )}
                      <Tooltip content={t('add_version')}>
                        <button
                          onClick={() => handleAddVariation(post)}
                          className="flex items-center justify-center p-1.5 transition-colors opacity-0 group-hover:opacity-100"
                          aria-label={t('add_new_version')}
                        >
                          <Icon name={ICONS.PLUS_CIRCLE} size="sm" color="text-dark-teal hover:text-bs-black" ariaHidden />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                  */}

                  {/* Status (Aggregated Instances) */}
                  <td className="hidden md:table-cell p-2 md:p-3">
                    <div className="flex items-center gap-1 text-xs flex-wrap">
                      {statusFlags.showMapped ? (
                        <Tooltip content={t('tooltip_status_mapped')}>
                          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded whitespace-nowrap">
                            📅 {post.instancesSummary?.mapped}
                          </span>
                        </Tooltip>
                      ) : null}
                      {post.instancesSummary?.scheduled && post.instancesSummary.scheduled > 0 ? (
                        <Tooltip content={t('status_scheduled')}>
                          <span className="px-1.5 py-0.5 bg-soft-mint text-dark-teal rounded whitespace-nowrap">
                            🚀 {post.instancesSummary.scheduled}
                          </span>
                        </Tooltip>
                      ) : null}
                      {/* First-class PUBLISHING bucket (Stage 2c-i / #182 B1) — was
                          previously counted in `total` but had no render branch,
                          leaving a blank cell for publishing-only occurrences. */}
                      {post.instancesSummary?.publishing && post.instancesSummary.publishing > 0 ? (
                        <Tooltip content={t('status_publishing')}>
                          <span
                            className={`px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-0.5 ${OCCURRENCE_STATUS_BADGES.PUBLISHING.bgColor} ${OCCURRENCE_STATUS_BADGES.PUBLISHING.color}`}
                          >
                            <Icon name={OCCURRENCE_STATUS_BADGES.PUBLISHING.icon} size="xs" ariaHidden />
                            {post.instancesSummary.publishing}
                          </span>
                        </Tooltip>
                      ) : null}
                      {statusFlags.showPublished ? (
                        <Tooltip content={t('status_published')}>
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded whitespace-nowrap">
                            ✅ {post.instancesSummary?.published || ''}
                          </span>
                        </Tooltip>
                      ) : null}
                      {post.instancesSummary?.partial && post.instancesSummary.partial > 0 ? (
                        <Tooltip content={t('status_partial')}>
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded whitespace-nowrap">
                            🟡 {post.instancesSummary.partial}
                          </span>
                        </Tooltip>
                      ) : null}
                      {post.instancesSummary?.failed && post.instancesSummary.failed > 0 ? (
                        <Tooltip content={t('status_failed')}>
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded whitespace-nowrap">
                            ⚠️ {post.instancesSummary.failed}
                          </span>
                        </Tooltip>
                      ) : null}
                      {post.instancesSummary?.missed && post.instancesSummary.missed > 0 ? (
                        <Tooltip content={t('status_missed')}>
                          <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded whitespace-nowrap">
                            ⌛ {post.instancesSummary.missed}
                          </span>
                        </Tooltip>
                      ) : null}
                      {/* First-class CANCELLED bucket (Stage 2c-i / #182 B1) — was
                          previously counted in `total` but had no render branch,
                          leaving a blank cell for cancelled-only occurrences. */}
                      {post.instancesSummary?.cancelled && post.instancesSummary.cancelled > 0 ? (
                        <Tooltip content={t('status_cancelled')}>
                          <span
                            className={`px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-0.5 ${OCCURRENCE_STATUS_BADGES.CANCELLED.bgColor} ${OCCURRENCE_STATUS_BADGES.CANCELLED.color}`}
                          >
                            <Icon name={OCCURRENCE_STATUS_BADGES.CANCELLED.icon} size="xs" ariaHidden />
                            {post.instancesSummary.cancelled}
                          </span>
                        </Tooltip>
                      ) : null}
                      {(!post.instancesSummary || post.instancesSummary.total === 0) && (
                        <span className="text-gray-400">
                          ☁️ {t('unmapped')}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Workflow Status (conditional) */}
                  {workflowEnabled && (
                    <td className="hidden md:table-cell p-2 md:p-3">
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs whitespace-nowrap ${statusConfig.color}`}>
                        <Icon name={statusConfig.icon} size="sm" ariaHidden />
                        <span>{statusConfig.label}</span>
                      </div>
                    </td>
                  )}

                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Video Preview Modal - Improved */}
      {videoPreview && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setVideoPreview(null)}
        >
          <div className="max-w-2xl w-full relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setVideoPreview(null)}
              className="absolute -top-12 right-0 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors backdrop-blur-sm flex items-center gap-2"
              aria-label={t('close_video_preview')}
            >
              <Icon name={ICONS.CLOSE} size="sm" ariaHidden />
              <span className="text-sm font-medium">{t('close')}</span>
            </button>
            <div className="bg-black rounded-xl shadow-2xl overflow-hidden max-h-[80vh]">
              {(videoPreview.sourceType === 'GDRIVE' && (!videoPreview.url || videoPreview.url === '') && videoPreview.sourceRef?.driveFileId) ? (
                <iframe
                  src={`https://drive.google.com/file/d/${videoPreview.sourceRef.driveFileId}/preview`}
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                  className="w-full max-h-[80vh] object-contain"
                  title="Google Drive preview"
                  style={{ height: '80vh' }}
                />
              ) : (
                <video
                  src={videoPreview.url ?? ''}
                  controls
                  autoPlay
                  className="w-full max-h-[80vh] object-contain"
                  aria-label="Video preview"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Media Carousel Modal */}
      {carouselMedia && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setCarouselMedia(null)}
        >
          <div className="max-w-4xl w-full relative" onClick={(e) => e.stopPropagation()}>
            {/* Header with close button */}
            <div className="absolute -top-12 left-0 right-0 flex items-center justify-between">
              <div className="px-4 py-2 bg-white/10 text-white rounded-lg backdrop-blur-sm">
                <span className="text-sm font-medium">
                  {carouselMedia.index + 1} / {carouselMedia.media.length}
                </span>
              </div>
              <button
                onClick={() => setCarouselMedia(null)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors backdrop-blur-sm flex items-center gap-2"
                aria-label={t('close_carousel')}
              >
                <Icon name={ICONS.CLOSE} size="sm" ariaHidden />
                <span className="text-sm font-medium">{t('close')}</span>
              </button>
            </div>

            {/* Main media display */}
            <div className="bg-black rounded-xl shadow-2xl overflow-hidden max-h-[80vh] flex items-center justify-center relative">
              {carouselMedia.media[carouselMedia.index].type === 'VIDEO' ? (
                (() => {
                  const cm = carouselMedia.media[carouselMedia.index];
                  return (cm.sourceType === 'GDRIVE' && (!cm.originalUrl || cm.originalUrl === '') && cm.sourceRef?.driveFileId) ? (
                    <iframe
                      src={`https://drive.google.com/file/d/${cm.sourceRef.driveFileId}/preview`}
                      allow="autoplay; encrypted-media; fullscreen"
                      allowFullScreen
                      className="w-full max-h-[80vh] object-contain"
                      title="Google Drive preview"
                      style={{ height: '80vh' }}
                    />
                  ) : (
                    <video
                      src={cm.originalUrl ?? ''}
                      controls
                      autoPlay
                      className="w-full max-h-[80vh] object-contain"
                      aria-label={`Video ${carouselMedia.index + 1} of ${carouselMedia.media.length}`}
                    />
                  );
                })()
              ) : (carouselMedia.media[carouselMedia.index].type === 'PDF' || carouselMedia.media[carouselMedia.index].type === 'DOCUMENT') && carouselMedia.media[carouselMedia.index].originalUrl ? (
                <div className="w-full h-[80vh] bg-white rounded-lg overflow-hidden">
                  <object
                    data={carouselMedia.media[carouselMedia.index].originalUrl ?? ''}
                    type="application/pdf"
                    className="w-full h-full"
                  >
                    <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                      <Icon name={ICONS.FILE} size="xl" className="text-gray-400" />
                      <p className="text-gray-600 text-center">
                        {t('unable_display_pdf')}
                      </p>
                      <a
                        href={carouselMedia.media[carouselMedia.index].originalUrl ?? ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-dark-teal hover:text-bs-black underline"
                      >
                        {t('open_pdf_new_tab')}
                      </a>
                    </div>
                  </object>
                </div>
              ) : (carouselMedia.media[carouselMedia.index].type === 'PDF' || carouselMedia.media[carouselMedia.index].type === 'DOCUMENT') ? (
                <FileThumbnailPlaceholder className="w-full h-[80vh]" iconSize="xl" />
              ) : (
                // For images: use originalUrl if materialized. For deferred
                // GDrive (no originalUrl) ask Drive for a high-res thumbnail
                // (auth via the user's signed-in Google session). Falling back
                // to the small default thumbnailUrl gave a ~200px preview in a
                // full-size modal — visually awful.
                (() => {
                  const cm = carouselMedia.media[carouselMedia.index];
                  const driveFileId = cm.sourceRef?.driveFileId;
                  const src =
                    cm.originalUrl ||
                    (driveFileId
                      ? `https://drive.google.com/thumbnail?id=${driveFileId}&sz=w2000`
                      : cm.thumbnailUrl) ||
                    undefined;
                  return (
                    <RetryImage
                      src={src}
                      alt={`Media ${carouselMedia.index + 1}`}
                      className="w-full max-h-[80vh] object-contain"
                    />
                  );
                })()
              )}
            </div>

            {/* Navigation arrows */}
            {carouselMedia.media.length > 1 && (
              <>
                {/* Left arrow */}
                <button
                  onClick={() => setCarouselMedia(prev =>
                    prev && prev.index > 0
                      ? { ...prev, index: prev.index - 1 }
                      : prev
                  )}
                  disabled={carouselMedia.index === 0}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center group"
                  aria-label={t('previous_media')}
                >
                  <Icon name={ICONS.ARROW_LEFT} size="md" color="text-gray-800" ariaHidden />
                </button>

                {/* Right arrow */}
                <button
                  onClick={() => setCarouselMedia(prev =>
                    prev && prev.index < prev.media.length - 1
                      ? { ...prev, index: prev.index + 1 }
                      : prev
                  )}
                  disabled={carouselMedia.index === carouselMedia.media.length - 1}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center group"
                  aria-label={t('next_media')}
                >
                  <Icon name={ICONS.ARROW_RIGHT} size="md" color="text-gray-800" ariaHidden />
                </button>
              </>
            )}

            {/* Thumbnail strip at bottom */}
            {carouselMedia.media.length > 1 && (
              <div className="mt-4 flex gap-2 justify-center overflow-x-auto pb-2">
                {carouselMedia.media.map((media, idx) => (
                  <button
                    key={media.id}
                    onClick={() => setCarouselMedia(prev => prev ? { ...prev, index: idx } : null)}
                    className={`relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 transition-all ${
                      idx === carouselMedia.index
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-black/60 scale-110'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    aria-label={t('view_media_n', { n: idx + 1 })}
                  >
                    {(media.type === 'PDF' || media.type === 'DOCUMENT') && !media.thumbnailUrl ? (
                      <FileThumbnailPlaceholder className="w-full h-full" iconSize="sm" />
                    ) : (
                      <RetryImage
                        src={
                          media.type === 'PDF' || media.type === 'DOCUMENT'
                            ? media.thumbnailUrl || undefined
                            : media.thumbnailUrl || media.originalUrl || undefined
                        }
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                    {media.type === 'VIDEO' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <span className="text-white text-xs">▶</span>
                      </div>
                    )}
                    {(media.type === 'PDF' || media.type === 'DOCUMENT') && media.thumbnailUrl && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <span className="text-white text-xs">📄</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialogSimple
        open={postToDelete !== null}
        onOpenChange={(open) => !open && setPostToDelete(null)}
        title={t('delete_post')}
        description={t('delete_post_confirm')}
        confirmText={t('delete')}
        cancelText={t('cancel')}
        variant="danger"
        onConfirm={handleConfirmDelete}
        showIcon={false}
      />

      {/* Variation Creator Modal */}
      <VariationCreator
        isOpen={variationCreator.isOpen}
        onClose={() =>
          setVariationCreator({
            isOpen: false,
            preSelectedPostId: null,
          })
        }
        preSelectedPostId={variationCreator.preSelectedPostId || undefined}
        onSuccess={() => {
          setVariationCreator({ isOpen: false, preSelectedPostId: null });
        }}
      />
    </div>
  );
};
